import { defaultConfig } from 'app/config/environment';

// Configuration
const IMAGE_SIZES = [400, 800];
const OPTIMIZED_FORMAT = 'avif';
// El separador tras el GUID puede ser '/' literal o '%2F' (slash URL-encoded en la clave S3)
const GUID_REGEX = /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|%2F)/i;

export const isS3Url = (url) => {
  return /\.s3(\.[^/]+)?\.amazonaws\.com\//i.test(url);
};

const extractS3GuidPath = (originalUrl) => {
  const guidMatch = originalUrl.match(GUID_REGEX);
  if (!guidMatch) return null;
  const guid = guidMatch[1];

  // El slash antes del GUID siempre es literal; el de después puede ser '/' o '%2F'
  const markerSlash = `/${guid}/`;
  const markerEncoded = `/${guid}%2F`;
  const slashIdx = originalUrl.indexOf(markerSlash);
  const encodedIdx = originalUrl.indexOf(markerEncoded);

  let startIndex;
  if (slashIdx !== -1 && (encodedIdx === -1 || slashIdx <= encodedIdx)) {
    startIndex = slashIdx + markerSlash.length;
  } else if (encodedIdx !== -1) {
    startIndex = encodedIdx + markerEncoded.length;
  } else {
    return null;
  }

  // Normalizar %2F a '/' para que las URLs proxy usen slashes literales
  const afterGuid = originalUrl.substring(startIndex).split('?')[0].replace(/%2F/gi, '/');
  return { guid, afterGuid };
};

export const buildS3ProxyUrl = (originalUrl, size, format, token) => {
  const parsed = extractS3GuidPath(originalUrl);
  if (!parsed) return null;
  const { guid, afterGuid } = parsed;

  const parts = afterGuid.split('/');
  const fileName = parts[parts.length - 1];
  const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  const subPath = parts.slice(0, -1).join('/');

  const optimizedFile = `${fileNameWithoutExtension}_file_${size}.${format}`;
  const fullPath = subPath
    ? `${guid}/${subPath}/${optimizedFile}`
    : `${guid}/${optimizedFile}`;

  const baseUrl = defaultConfig.BASE_URL_;
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${baseUrl}files-storage/open/${fullPath}${tokenParam}`;
};

export const buildS3ProxySrcUrl = (originalUrl, token) => {
  const parsed = extractS3GuidPath(originalUrl);
  if (!parsed) return null;
  const { guid, afterGuid } = parsed;

  const baseUrl = defaultConfig.BASE_URL_;
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${baseUrl}files-storage/open/${guid}/${afterGuid}${tokenParam}`;
};

export const isOptimizableUrl = (url) => {
  if (!url || typeof url !== 'string') return false;

  // Omitir imágenes locales
  const localPatterns = [/^\/images\//, /^\.\/images\//, /^\.\.\/images\//, /^\/assets\//, /^blob:/, /^data:image\//];

  if (localPatterns.some((pattern) => pattern.test(url))) {
    return false;
  }

  // Solo URLs absolutas (S3)
  return /^https?:\/\//.test(url);
};

export const extractFileInfo = (url) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1) return null;

    return {
      name: filename.substring(0, lastDotIndex),
      extension: filename.substring(lastDotIndex + 1),
      basePath: url.substring(0, url.lastIndexOf('/')),
      queryParams: urlObj.search,
    };
  } catch {
    return null;
  }
};

export const generateSrcSet = (url, sizes = IMAGE_SIZES, maxSizeIndex = 0, availableSizes = null, token = null) => {
  if (!isOptimizableUrl(url)) return null;

  const fileInfo = extractFileInfo(url);
  if (!fileInfo) return null;

  const { name, basePath, queryParams } = fileInfo;

  // Filtrar tamaños disponibles si se proporcionan
  let sizesToUse = availableSizes || sizes;

  // Excluir tamaños más grandes según maxSizeIndex
  // maxSizeIndex = 0: incluir todos
  // maxSizeIndex = 1: excluir el último (más grande)
  // maxSizeIndex = 2: excluir los últimos 2, etc.
  if (maxSizeIndex > 0 && maxSizeIndex < sizesToUse.length) {
    sizesToUse = sizesToUse.slice(0, sizesToUse.length - maxSizeIndex);
  }

  const srcSetEntries = sizesToUse.map((size) => {
    if (isS3Url(url)) {
      if (!token) return null;
      const proxyUrl = buildS3ProxyUrl(url, size, OPTIMIZED_FORMAT, token);
      return proxyUrl ? `${proxyUrl} ${size}w` : null;
    }
    const optimizedFilename = `${name}_file_${size}.${OPTIMIZED_FORMAT}`;
    const optimizedUrl = `${basePath}/${optimizedFilename}${queryParams}`;
    return `${optimizedUrl} ${size}w`;
  });

  const filtered = srcSetEntries.filter(Boolean);
  return filtered.length > 0 ? filtered.join(', ') : null;
};

export const generateSizes = (breakpoints = null) => {
  const defaultSizes = ['(max-width: 640px) 100vw', '(max-width: 768px) 80vw', '(max-width: 1024px) 60vw', '50vw'];

  return breakpoints ? breakpoints.join(', ') : defaultSizes.join(', ');
};

export const createFallbackHandler = (originalSrc, onError) => {
  let fallbackAttempted = false;

  return (event) => {
    if (!fallbackAttempted && event.target) {
      fallbackAttempted = true;

      // Eliminar srcSet para que navegador no reintente variantes
      event.target.removeAttribute('srcset');
      event.target.removeAttribute('sizes');

      // Volver a imagen original
      event.target.src = originalSrc;

      // Llamar handler personalizado si existe
      if (onError) {
        onError(event);
      }
    }
  };
};
